import React, { useEffect, useRef, useState } from "react";
import { dollars } from "../formatMoney";

export function clampDollarValue(value, maxDollars) {
  return Math.max(0, Math.min(Number.isFinite(value) ? value : 0, maxDollars));
}

export function integerDigitCount(maxDollars) {
  const safeMax = Math.max(0, Number(maxDollars) || 0);
  return Math.max(1, Math.floor(safeMax).toString().length);
}

export function formatDollarDemandDisplay(value, maxDollars, integerDigits) {
  if (!Number.isFinite(Number(value))) {
    return "-.--";
  }

  const clamped = clampDollarValue(value, maxDollars);
  const [integerPart, decimalPart] = clamped.toFixed(2).split(".");
  const paddedInteger = integerPart.padStart(integerDigits, "0").slice(-integerDigits);
  return `${paddedInteger}.${decimalPart}`;
}

export function parseDollarDemandDisplay(displayValue) {
  if (displayValue === null || displayValue === undefined || displayValue === "") {
    return Number.NaN;
  }

  const numeric = Number(displayValue);
  return Number.isFinite(numeric) ? numeric : Number.NaN;
}

export function DollarDemandInput({
  id,
  "aria-describedby": ariaDescribedBy,
  value,
  maxDollars,
  onChange,
  disabled,
  className = "appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-empirica-500 focus:border-empirica-500 text-2xl",
}) {
  const inputRef = useRef(null);
  const [activeDigitPosition, setActiveDigitPosition] = useState(0);
  const integerDigits = integerDigitCount(maxDollars);
  const summaryId = `${id}-summary`;
  const parsedValue = parseDollarDemandDisplay(value);
  const leftOver = Number.isFinite(parsedValue)
    ? Math.max(0, maxDollars - clampDollarValue(parsedValue, maxDollars))
    : Number.NaN;
  const editableIndices = [
    ...Array.from({ length: integerDigits }, (_, index) => index),
    integerDigits + 1,
    integerDigits + 2,
  ];

  useEffect(() => {
    const input = inputRef.current;
    if (!input || document.activeElement !== input) {
      return;
    }

    const index = editableIndices[activeDigitPosition] ?? editableIndices[0] ?? 0;
    input.setSelectionRange(index, index + 1);
  }, [activeDigitPosition, editableIndices, value]);

  function setFormattedValue(nextNumericValue) {
    const clamped = clampDollarValue(nextNumericValue, maxDollars);
    onChange(formatDollarDemandDisplay(clamped, maxDollars, integerDigits));
  }

  function selectDigit(position) {
    const input = inputRef.current;
    const index = editableIndices[position] ?? editableIndices[0] ?? 0;

    setActiveDigitPosition(position);
    input?.setSelectionRange(index, index + 1);
  }

  function handleChange(nextValue) {
    const numeric = Number(nextValue);
    if (!Number.isFinite(numeric)) {
      return;
    }

    setFormattedValue(numeric);
  }

  function handleKeyDown(event) {
    const { key } = event;

    if (/^[0-9]$/.test(key)) {
      event.preventDefault();
      const targetIndex = editableIndices[activeDigitPosition] ?? editableIndices[0];
      if (targetIndex === undefined) {
        return;
      }

      const numericValue = parseDollarDemandDisplay(value);
      const chars = formatDollarDemandDisplay(
        Number.isFinite(numericValue) ? numericValue : 0,
        maxDollars,
        integerDigits
      ).split("");
      chars[targetIndex] = key;

      const candidateValue = parseDollarDemandDisplay(chars.join(""));
      setFormattedValue(candidateValue);

      const atMax = candidateValue > maxDollars;
      if (atMax) {
        setActiveDigitPosition(editableIndices.length - 1);
        return;
      }

      setActiveDigitPosition((position) =>
        Math.min(position + 1, editableIndices.length - 1)
      );
      return;
    }

    if (key === "Backspace") {
      event.preventDefault();
      if (!Number.isFinite(parseDollarDemandDisplay(value))) {
        return;
      }

      const targetIndex = editableIndices[activeDigitPosition] ?? editableIndices[0];
      if (targetIndex !== undefined) {
        const chars = value.split("");
        chars[targetIndex] = "0";
        setFormattedValue(parseDollarDemandDisplay(chars.join("")));
      }
      setActiveDigitPosition((position) => Math.max(position - 1, 0));
      return;
    }

    if (key === "Delete") {
      event.preventDefault();
      if (!Number.isFinite(parseDollarDemandDisplay(value))) {
        return;
      }

      const targetIndex = editableIndices[activeDigitPosition] ?? editableIndices[0];
      if (targetIndex === undefined) {
        return;
      }

      const chars = value.split("");
      chars[targetIndex] = "0";
      setFormattedValue(parseDollarDemandDisplay(chars.join("")));
      return;
    }

    if (key === "ArrowLeft") {
      event.preventDefault();
      setActiveDigitPosition((position) => Math.max(position - 1, 0));
      return;
    }

    if (key === "ArrowRight") {
      event.preventDefault();
      setActiveDigitPosition((position) =>
        Math.min(position + 1, editableIndices.length - 1)
      );
    }
  }

  function handleFocus() {
    selectDigit(0);
  }

  function handleClick() {
    selectDigit(0);
  }

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <div className="relative max-w-full shrink-0">
        <span
          className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-2xl text-gray-900"
          aria-hidden="true"
        >
          $
        </span>
        <input
          id={id}
          aria-describedby={[ariaDescribedBy, summaryId].filter(Boolean).join(" ")}
          ref={inputRef}
          className={className}
          style={{
            paddingLeft: "2.5rem",
            width: `calc(${integerDigits + 3}ch + 4rem)`,
            maxWidth: "100%",
          }}
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(event) => handleChange(event.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onClick={handleClick}
          disabled={disabled}
        />
      </div>
      <p
        id={summaryId}
        className="flex-1 text-2xl text-gray-700"
        style={{ minWidth: "min(100%, 16rem)" }}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        requested for yourself.{" "}
        <span className="inline-block whitespace-nowrap tabular-nums">
          {dollars(leftOver)} left over.
        </span>
      </p>
    </div>
  );
}
